import React, { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import InterestFreeOption from './InterestFreeOption'
import {
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
  getKlarnaBpMaxLimit,
  isKlarnaBpEnabled,
  isWithinKlarnaBpAvailabilityRange,
  isLatitudePayEnabled,
  isLatitudeFinanceEnabled,
  isLatitudeGemEnabled,
} from 'lib/paymentUtils'
import {
  KLARNA_PAYMENT_TYPE,
  LATITUDE_PAYMENT_TYPE,
  KLARNA_BP_PAYMENT_TYPE,
  HOOLAH_BP_PAYMENT_TYPE,
  ATOME_BP_PAYMENT_TYPE,
  LATITUDE_GEM_PAYMENT_TYPE,
  LATITUDE_PAY_PAYMENT_TYPE,
  AFTER_PAY_BP_PAYMENT_TYPE,
  WAAVE_BP_PAYMENT_TYPE,
} from 'constants/payment'
import { connect } from 'react-redux'
import { getAfterPayBpMaxLimit, isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/afterPayBpUtils'
import { getHoolahBpMaxLimit, isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/hoolahBpUtils'
import { getAtomeBpMaxLimit, isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/waaveBpUtils'
import { formatRoundedAmount, formatRoundedAmountWithDollarType } from 'lib/formatCurrencyIntl'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { getSVGImageUrl } from 'lib/imageUtils'
import Modal from 'components/Luxkit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import GeoContext from 'contexts/geoContext'

const HowToUseSection = styled.div`
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  max-width: ${rem(680)};
`

const PaymentOptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  > * + * {
    margin-top: ${rem(12)};
  }
`

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  currency: string;
  offer?: App.Offer;
}

function InterestFreeLearnMoreModal(props: Props) {
  const { isOpen, onClose, currency, offer } = props

  const { currentRegionCode } = useContext(GeoContext)

  const products = [
    {
      id: KLARNA_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt="Klarna Pay" height="40" />,
      brand: 'Klarna',
      title: 'Pay over 4 interest-free instalments',
      content: 'For purchases under $2,500',
      to: '/klarna',
      isAvailable: (currency, offer) => isKlarnaEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaAvailabilityRange(p.price))),
    },
    {
      id: KLARNA_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-klarna')} alt="Klarna Pay" height="40" />,
      brand: 'Klarna',
      title: currency === 'GBP' ? 'Pay over 3 interest-free instalments' : 'Pay over 4 interest-free instalments',
      content: `For purchases under ${formatRoundedAmount(getKlarnaBpMaxLimit(currency), currency, currentRegionCode)}`,
      to: '/klarna',
      isAvailable: (currency, offer) => isKlarnaBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinKlarnaBpAvailabilityRange(p.price, currency))),
    },
    {
      id: LATITUDE_PAYMENT_TYPE,
      image: <img height="40" src={getSVGImageUrl('payments-latitude')} alt="Apply for a Latitude Gem Visa Credit Card" />,
      brand: 'Latitude',
      title: '12 months interest-free*',
      content: 'For purchases over $999',
      to: '/latitude',
      conditions: 'Monthly payments required. New customers must apply and be approved for a Latitude Gem Visa credit card. Available on participating Latitude credit cards only. Conditions, fees, charges apply',
      isAvailable: (currency) => isLatitudeFinanceEnabled(currency),
    },
    {
      id: LATITUDE_GEM_PAYMENT_TYPE,
      image: <img height="40" src={getSVGImageUrl('payments-latitude')} alt="Apply for a Latitude Gem Visa Credit Card" />,
      brand: 'Latitude',
      title: '12 months interest-free*',
      content: 'For purchases over $999',
      to: '/latitude',
      conditions: 'Monthly payments required. New customers must apply and be approved for a Latitude Gem Visa credit card. Available on participating Latitude credit cards only. Conditions, fees, charges apply',
      isAvailable: (currency) => isLatitudeGemEnabled(currency),
    },
    {
      id: LATITUDE_PAY_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-latitudepay')} alt="Latitude Pay" height="40" />,
      brand: 'Latitude Pay',
      title: 'Pay over 10 weekly payments. No interest. Ever.',
      content: 'For purchases under $999',
      to: '/latitude',
      isAvailable: (currency) => isLatitudePayEnabled(currency),
    },
    {
      id: AFTER_PAY_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-afterpay')} alt="After Pay" height="40" />,
      brand: 'After pay',
      title: 'Pay over 4 interest-free instalments',
      content: `For purchases under ${formatRoundedAmount(getAfterPayBpMaxLimit(currency), currency, currentRegionCode)}`,
      to: '/afterpay',
      isAvailable: (currency) => isAfterPayBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAfterPayBpAvailabilityRange(p.price, currency))),
    },
    {
      id: HOOLAH_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt="Shopback" height="40" />,
      brand: 'Shopback',
      title: 'Pay in three instalments',
      content: `For purchases ${formatRoundedAmountWithDollarType(getHoolahBpMaxLimit(currency), currency, currentRegionCode)} and under`,
      to: '/shopbackpaylater',
      isAvailable: (currency) => isHoolahBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinHoolahBpAvailabilityRange(p.price, currency))),
    },
    {
      id: ATOME_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-placeholder')} alt="Atome" height="40" />,
      brand: 'Atome',
      title: 'Pay in three instalments',
      content: `For purchases ${formatRoundedAmountWithDollarType(getAtomeBpMaxLimit(currency), currency, currentRegionCode)} and under`,
      to: '/atome',
      isAvailable: (currency) => isAtomeBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinAtomeBpAvailabilityRange(p.price, currency))),
    },
    {
      id: WAAVE_BP_PAYMENT_TYPE,
      image: <img src={getSVGImageUrl('payments-waave')} alt="LuxPay" height="15" />,
      brand: 'LuxPay, powered by Waave',
      title: 'Pay securely via direct debit',
      content: 'No minimum spend.',
      to: '/luxpay',
      isAvailable: (currency) => isWaaveBpEnabled(currency) && (!offer || offer.packages.some(p => isWithinWaaveBpAvailabilityRange(p.price, currency))),
    },
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Interest free payment options"
      subtitle="Select from the following interest free payment options in checkout."
    >
      <VerticalSpacer gap={20}>
        <PaymentOptionSection>
          {products.map(product => {
            if (product.isAvailable(currency, offer)) {
              return (<InterestFreeOption
                  key={product.id}
                  product={product}
              />)
            }
            return null
          })}
        </PaymentOptionSection>
        <HowToUseSection>
          <Heading variant="heading6">How to use this service</Heading>
          <BodyText variant="medium">
            Select your Escape and checkout as normal. In the checkout select the{' '}
            interest free payment option and continue from there.
          </BodyText>
        </HowToUseSection>
      </VerticalSpacer>
    </Modal>
  )
}

function mapStateToProps(state: App.State) {
  return {
    currency: state.geo.currentCurrency,
  }
}

export default connect(mapStateToProps)(InterestFreeLearnMoreModal)
